import type { ReactNode } from 'react';
import {
  isRpExternalLink,
  resolveRpEmbed,
  resolveRpLink,
  type RpButton,
  type RpMediaRef,
} from '@mlm/shared';
import { cn } from '../lib/cn';
import { useRpCtx } from './context';
import { RP_ASPECT, RP_BTN_BASE, RP_BTN_SIZE, RP_BTN_VARIANT } from './tokens';

/**
 * Shared building blocks for replicated-page sections. Kept here rather than in
 * each section so that a button rendered inside a hero, a CTA band and a
 * standalone button group is provably the same button.
 */

/** One typed button. The href is DERIVED — a section never stores a raw URL for it. */
export function RpLinkButton({ button, className }: { button: RpButton; className?: string }) {
  const ctx = useRpCtx();
  const href = resolveRpLink(button.link, ctx.link);
  const external = isRpExternalLink(button.link);
  return (
    <a
      href={href}
      className={cn(
        RP_BTN_BASE,
        RP_BTN_VARIANT[button.variant ?? 'primary'] ?? RP_BTN_VARIANT.primary,
        RP_BTN_SIZE[button.size ?? 'md'] ?? RP_BTN_SIZE.md,
        className,
      )}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {button.label}
    </a>
  );
}

/** True when a media ref actually points at something renderable. */
export function hasRpMedia(media: RpMediaRef | undefined): boolean {
  if (!media) return false;
  if (media.kind === 'image') return !!media.imageKey;
  if (media.kind === 'legacyImage') return !!media.blockId;
  return !!media.url;
}

/** An image or video reference → an element. Returns null when unresolved. */
export function RpMedia({
  media,
  className,
  aspect,
}: {
  media: RpMediaRef | undefined;
  className?: string;
  aspect?: '16:9' | '9:16' | '4:3';
}) {
  const ctx = useRpCtx();
  if (!media) return null;

  if (media.kind === 'video') {
    if (!media.url) return null;
    const embed = resolveRpEmbed(media.url);
    return (
      <div className={cn(RP_ASPECT[aspect ?? '16:9'], 'w-full overflow-hidden rounded-lg bg-black', className)}>
        {embed.kind === 'video' ? (
          <video src={embed.src} controls className="h-full w-full" />
        ) : (
          <iframe
            src={embed.src}
            title="video"
            className="h-full w-full"
            // Vimeo's and YouTube's own recommended embed attributes, unioned —
            // one element renders both hosts. Permissions Policy is DELEGATED,
            // not inherited: without `allow` the player silently loses autoplay,
            // fullscreen, PiP, casting and EME (DRM).
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            // Vimeo's "Only on domains I choose" privacy check reads the REFERER
            // of this request. This is the browser default today — pinned so a
            // future `Referrer-Policy: no-referrer` security header can't
            // silently 403 every domain-locked embed on the platform.
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
          />
        )}
      </div>
    );
  }

  const src =
    media.kind === 'legacyImage' ? ctx.legacyImageUrl(media.blockId) : media.imageKey ? ctx.imageUrl(media.imageKey) : '';
  if (!src) return null;
  return <img src={src} alt={media.alt ?? ''} className={cn('max-w-full rounded-lg', className)} />;
}

/** Eyebrow + heading + subheading, the header every content section shares. */
export function RpSectionHeader({
  eyebrow,
  heading,
  subheading,
  className,
}: {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  className?: string;
}) {
  if (!eyebrow && !heading && !subheading) return null;
  return (
    <div className={cn('space-y-2', className)}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-widest opacity-70">{eyebrow}</p>
      ) : null}
      {heading ? <h2 className="font-heading text-2xl font-bold sm:text-3xl">{heading}</h2> : null}
      {subheading ? <p className="text-base opacity-80">{subheading}</p> : null}
    </div>
  );
}

/** Plain multi-paragraph body copy: blank lines split paragraphs. */
export function RpParagraphs({ text, className }: { text?: string; className?: string }) {
  if (!text?.trim()) return null;
  const paras = text.split(/\n{2,}/);
  return (
    <div className={cn('space-y-3', className)}>
      {paras.map((p, i) => (
        <p key={i} className="whitespace-pre-wrap text-base opacity-90">
          {p}
        </p>
      ))}
    </div>
  );
}

/** A checkmark bullet list, capped by the caller. */
export function RpBullets({ items, className }: { items?: readonly string[]; className?: string }) {
  if (!items?.length) return null;
  return (
    <ul className={cn('space-y-2', className)}>
      {items.map((b, i) => (
        <li key={i} className="flex items-start gap-2 text-base">
          <span aria-hidden className="mt-1 text-primary">
            ✓
          </span>
          <span className="opacity-90">{b}</span>
        </li>
      ))}
    </ul>
  );
}

/** Wraps children only when `when` is true — keeps section JSX flat. */
export function RpMaybe({ when, children }: { when: boolean; children: ReactNode }) {
  return when ? <>{children}</> : null;
}
